import React, { useState, useRef, useEffect, useContext } from 'react';
import { 
  Flex, 
  Textarea,  
  Button, 
  Box, 
  Text, 
  Spinner,
  Avatar,
  useBreakpointValue,
  SimpleGrid,
  useToast,
  IconButton
} from '@chakra-ui/react';
import GPTresposeImg from "assets/img/aiavatar.png";
import UserSearchImg from "assets/img/user_avatar.png";
import axios from "axios";
import { marked } from 'marked'; 
import { AppContext } from '../../../context/AppContext';
import { 
  QuerySendButton, 
  SuggestionsVector, 
  EditIcon, 
  GridGLogo, 
  MobileUserLogo, 
  UploadImagesChat,
  MobileImgUpload,
  MobileVoiceUpload,
  ImgCrossIcon,
 } from 'components/Icons/Icons';
import  LoadingModal2  from './LoadingModal2';
import WaveSurfer from 'wavesurfer.js';
import ChatVoiceMessage from './ChatVoiceMessage';
import useRecording from './index';
import { useAuth } from "auth/authProvider";
import { getUserSessionHistory } from 'auth/session_history/chatSession';
import Cookies from "js-cookie";

const CHAT_API_URL = process.env.REACT_APP_CHATAPI_URL;


// MODE TOGGLE COMPONENT - Esports / eJourney

const ModeToggle = ({ mode, setMode }) => (
  <Flex
    align="center"
    justify="center"
    bg="white"
    borderRadius="full"
    p={1}
    boxShadow="0 2px 8px rgba(0,0,0,0.08)"
    border="1px solid #e8eaed"
    w="fit-content"
  >
    <Button
      size="sm"
      borderRadius="full"
      px={5}
      py={2}
      fontWeight="600"
      fontSize="sm"
      bg={mode === 'esports' ? '#298BE2' : 'transparent'}
      color={mode === 'esports' ? 'white' : 'gray.500'}
      _hover={{
        bg: mode === 'esports' ? '#298BE2' : 'gray.100',
      }}
      onClick={() => setMode('esports')}
      transition="all 0.2s ease"
    >
      🎮 Esports
    </Button>
    <Button
      size="sm"
      borderRadius="full"
      px={5}
      py={2}
      fontWeight="600"
      fontSize="sm"
      bg={mode === 'ejourney' ? '#1069B8' : 'transparent'}
      color={mode === 'ejourney' ? 'white' : 'gray.500'}
      _hover={{
        bg: mode === 'ejourney' ? '#1069B8' : 'gray.100',
      }}
      onClick={() => setMode('ejourney')}
      transition="all 0.2s ease"
    >
      ✈️ eJourney
    </Button>
  </Flex>
);

// PLACE CARDS FEATURE - Helper Functions & Components


/**
 * Parses HTML response (converted from markdown by marked()) to extract place data
 * Returns null if the response doesn't contain place information or is still streaming
 * 
 * Expected HTML structure from marked():
 * <ol>
 *   <li><p><strong>Place Name</strong></p>
 *     <ul>
 *       <li><strong>Rating</strong>: 4.9</li>
 *       <li><strong>Address</strong>: ...</li>
 *       <li><img src="https://places.googleapis.com/..." alt="Place Name"></li>
 *       <li><a href="https://www.google.com/maps/...">View on Google Maps</a></li>
 *     </ul>
 *   </li>
 * </ol>
 */
const parsePlacesFromMarkdown = (htmlContent) => {
  // Safety check for empty/invalid input
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  // Check if this looks like a COMPLETE places response
  // Must have Rating, Address, image from googleapis, and Google Maps link
  if (!htmlContent.includes('<strong>Rating</strong>:') || 
      !htmlContent.includes('View on Google Maps</a>') ||
      !htmlContent.includes('places.googleapis.com')) {
    return null;
  }

  // Wait for the response to be complete (ends with closing tags or "Enjoy")
  // This prevents parsing during streaming
  if (!htmlContent.includes('</ol>') && !htmlContent.includes('Enjoy')) {
    return null;
  }

  const places = [];
  
  // Split by top-level <li> tags in the ordered list
  // Each place starts with <li><p><strong>Name</strong></p>
  const placeRegex = /<li><p><strong>([^<]+)<\/strong><\/p>\s*<ul>([\s\S]*?)<\/ul>\s*<\/li>/g;
  
  let match;
  while ((match = placeRegex.exec(htmlContent)) !== null) {
    try {
      const name = match[1].trim();
      const detailsBlock = match[2];
      
      // Extract rating: <li><strong>Rating</strong>: 4.9</li>
      const ratingMatch = detailsBlock.match(/<strong>Rating<\/strong>:\s*([\d.]+)/);
      
      // Extract address: <li><strong>Address</strong>: address text</li>
      const addressMatch = detailsBlock.match(/<strong>Address<\/strong>:\s*([^<]+)/);
      
      // Extract image URL: <img src="https://places.googleapis.com/..." alt="...">
      const imageMatch = detailsBlock.match(/<img\s+src="(https:\/\/places\.googleapis\.com[^"]+)"/);
      
      // Extract Google Maps link: <a href="https://www.google.com/maps/...">View on Google Maps</a>
      const mapsLinkMatch = detailsBlock.match(/<a\s+[^>]*href="(https:\/\/www\.google\.com\/maps[^"]+)"[^>]*>/);

      places.push({
        name: name,
        rating: ratingMatch ? parseFloat(ratingMatch[1]) : null,
        address: addressMatch ? addressMatch[1].trim() : null,
        imageUrl: imageMatch ? imageMatch[1] : null,
        mapsLink: mapsLinkMatch ? mapsLinkMatch[1] : null,
      });
    } catch (e) {
      console.error('Error parsing place block:', e);
    }
  }

  return places.length > 0 ? places : null;
};

/**
 * Parses HTML response for image gallery format
 * Expected markdown format: [![Title](image-url)](source-url)
 * With **Title:**, **Description:**, **Source:** fields
 * 
 * After marked() conversion, the HTML looks like:
 * <li><a href="source"><img src="image" alt="title"></a><br><strong>Title:</strong> text<br><strong>Source:</strong> <a href="...">name</a></li>
 */
const parseImagesFromMarkdown = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  // Check if this looks like an image response
  // Note: marked() puts the colon INSIDE the <strong> tag: <strong>Title:</strong>
  if (!htmlContent.includes('<strong>Title:</strong>') || 
      !htmlContent.includes('<strong>Source:</strong>')) {
    return null;
  }

  // Wait for response to be mostly complete (at least 3 images)
  const imageCount = (htmlContent.match(/<img\s+src="/g) || []).length;
  if (imageCount < 3) {
    return null;
  }

  const images = [];
  
  // Parse each list item containing an image
  const itemRegex = /<li>([\s\S]*?)<\/li>/g;
  
  let match;
  while ((match = itemRegex.exec(htmlContent)) !== null) {
    try {
      const block = match[1];
      
      // Extract image URL: <img src="..." alt="...">
      const imgMatch = block.match(/<img\s+src="([^"]+)"\s+alt="([^"]*)"/);
      if (!imgMatch) continue;
      
      const imageUrl = imgMatch[1];
      const altText = imgMatch[2];
      
      // Extract title: <strong>Title:</strong> text<br>
      const titleMatch = block.match(/<strong>Title:<\/strong>\s*([^<]*)/);
      
      // Extract description: <strong>Description:</strong> text<br>
      const descMatch = block.match(/<strong>Description:<\/strong>\s*([^<]*)/);
      
      // Extract source link: <strong>Source:</strong> <a href="...">name</a>
      const sourceMatch = block.match(/<strong>Source:<\/strong>\s*<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>/);
      
      // Extract the main link wrapping the image
      const linkMatch = block.match(/<a\s+href="([^"]+)"[^>]*>\s*<img/);

      images.push({
        imageUrl: imageUrl,
        title: titleMatch ? titleMatch[1].trim() : altText || 'Image',
        description: descMatch ? descMatch[1].trim() : '',
        sourceUrl: sourceMatch ? sourceMatch[1] : (linkMatch ? linkMatch[1] : null),
        sourceName: sourceMatch ? sourceMatch[2] : 'View Source',
      });
    } catch (e) {
      console.error('Error parsing image block:', e);
    }
  }

  return images.length > 0 ? images : null;
};

/**
 * Individual image card component
 */
const ImageCard = ({ image }) => {
  // Ensure we have a valid URL to link to
  const linkUrl = image.sourceUrl || image.imageUrl;
  
  return (
    <Box
      borderRadius="20px"
      overflow="hidden"
      bg="white"
      border="1px solid #e8eaed"
      boxShadow="0 2px 8px rgba(0,0,0,0.1)"
      transition="transform 0.2s ease, box-shadow 0.2s ease"
      _hover={{
        transform: 'translateY(-3px)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
      }}
      w="100%"
      maxW="600px"
      mb={4}
    >
      {image.imageUrl && (
        <Box 
          w="100%" 
          h="200px"
          p={3}
          pb={0}
        >
          <a 
            href={linkUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'block', width: '100%', height: '100%' }}
            onClick={(e) => {
              if (!linkUrl) {
                e.preventDefault();
                console.log('No URL available for this image');
              }
            }}
          >
            <img
              src={image.imageUrl}
              alt={image.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '16px',
                cursor: linkUrl ? 'pointer' : 'default',
              }}
              onError={(e) => {
                e.target.parentElement.parentElement.style.display = 'none';
              }}
            />
          </a>
        </Box>
      )}
      <Box p={4} pt={3}>
        <Text 
          fontWeight="700" 
          fontSize="md" 
          mb={1} 
          noOfLines={2}
          color="gray.800"
          lineHeight="1.3"
        >
          {image.title}
        </Text>
        
        {image.description && (
          <Text 
            fontSize="sm" 
            color="gray.500" 
            noOfLines={2}
            lineHeight="1.4"
          >
            {image.description}
          </Text>
        )}
      </Box>
    </Box>
  );
};

/**
 * Vertical container for image cards
 */
const ImagesGrid = ({ images }) => (
  <Flex 
    direction="column"
    w="100%"
  >
    {images.map((image, index) => (
      <ImageCard key={index} image={image} />
    ))}
  </Flex>
);

/**
 * Individual place card component - rectangular layout with prominent image
 */
const PlaceCard = ({ place }) => (
  <Box
    borderRadius="20px"
    overflow="hidden"
    bg="white"
    border="1px solid #e8eaed"
    boxShadow="0 2px 8px rgba(0,0,0,0.1)"
    transition="transform 0.2s ease, box-shadow 0.2s ease"
    _hover={{
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    }}
    w="100%"
    maxW="600px"
    mb={4}
  >
    {place.imageUrl && (
      <Box 
        w="100%" 
        h="180px"
        p={3}
        pb={0}
      >
        <img
          src={place.imageUrl}
          alt={place.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '16px',
          }}
          onError={(e) => {
            e.target.parentElement.style.display = 'none';
          }}
        />
      </Box>
    )}
    <Box p={4} pt={3}>
      <Text 
        fontWeight="700" 
        fontSize="md" 
        mb={1} 
        noOfLines={1}
        color="gray.800"
        lineHeight="1.3"
      >
        {place.name}
      </Text>
      
      {place.rating && (
        <Flex align="center" mb={1}>
          <Box color="#FBBC04" mr={1} fontSize="sm">★</Box>
          <Text fontSize="sm" color="gray.600" fontWeight="500">{place.rating}</Text>
        </Flex>
      )}
      
      {place.address && (
        <Text 
          fontSize="sm" 
          color="gray.500" 
          mb={3} 
          noOfLines={1}
          lineHeight="1.4"
        >
          {place.address}
        </Text>
      )}
      
      {place.mapsLink && (
        <Flex justify="flex-end">
          <Button
            as="a"
            href={place.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            bg="#298BE2"
            color="white"
            borderRadius="full"
            px={4}
            fontSize="sm"
            fontWeight="500"
            _hover={{
              bg: '#1a7fd4',
            }}
          >
            View on Maps
          </Button>
        </Flex>
      )}
    </Box>
  </Box>
);

/**
 * Vertical container for place cards
 */
const PlacesGrid = ({ places }) => (
  <Flex 
    direction="column"
    w="100%"
  >
    {places.map((place, index) => (
      <PlaceCard key={index} place={place} />
    ))}
  </Flex>
);

/**
 * Response renderer that handles places, image galleries, and regular markdown
 */
const ResponseContent = ({ content, messagesEndRef }) => {
  // Try parsing as places first (Google Places API format)
  const places = parsePlacesFromMarkdown(content || '');
  if (places) {
    return (
      <>
        <Text mb={3} fontWeight="500" color="gray.700">
          Here are some top places to visit:
        </Text>
        <PlacesGrid places={places} />
        <div ref={messagesEndRef} />
      </>
    );
  }
  
  // Try parsing as image gallery
  const images = parseImagesFromMarkdown(content || '');
  if (images) {
    return (
      <>
        <Text mb={3} fontWeight="500" color="gray.700">
          Here are the images I found:
        </Text>
        <ImagesGrid images={images} />
        <div ref={messagesEndRef} />
      </>
    );
  }
  
  // Default: render as regular markdown
  return (
    <>
      <Text 
        dangerouslySetInnerHTML={{ __html: content }} 
        sx={{
          a: { 
            color: '#298be2', 
            textDecoration: 'underline', 
            _hover: { 
              color: 'darkblue' 
            }
          }
        }}
      />
      <div ref={messagesEndRef} />
    </>
  );
};

// ============================================
// MAIN COMPONENT - ChatInterface
// ============================================

const ChatInterface = () => {
  const { 
          sideMessages, 
          setSideMessages,
          messages,
          setMessages,
          responseMessages,
          setResponseMessages,
          showSuggestions,
          setShowSuggestions
        } = useContext(AppContext);

  const [message, setMessage] = useState(''); 
  const [loading, setLoading] = useState(false); 
  const [editIndex, setEditIndex] = useState(null);
  const size = useBreakpointValue({ sm: "sm", lg: "lg" });
  const textareaRef = useRef(null);
  const [currentTimestamp, setCurrentTimestamp] = useState('');
  const lastSentTimeRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const messagesEndRef = useRef(null); 
  const toast = useToast()
  const [file, setFile] = useState();
  const [allimages, setAllImages] = useState([]);
  const [bodyFormData, setBodyFormData] = useState();
  const [imageSubmitted, setImageSubmitted] = useState(false);
  const waveSurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const waveContainerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const [recordings, setRecordings] = useState([]);
  const waveformRefs = useRef([]);
  const { isListening, transcript, startListening, stopListening, setIsListening, setTranscript } = useRecording({continuous: true})
  const [deviceType, setDeviceType] = useState("");
  const { user, chatSubscription } = useAuth();
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const fileInputRef = useRef(null);
  const [messageCounter, setMessageCounter] = useState(1);
  const [mode, setMode] = useState('ejourney'); // Toggle state: 'esports' or 'ejourney'
  
  // Separate message tracking for each mode (visual only - same API)
  const [esportsMessages, setEsportsMessages] = useState([]);
  const [esportsResponses, setEsportsResponses] = useState([]);
  const [ejourneyMessages, setEjourneyMessages] = useState([]);
  const [ejourneyResponses, setEjourneyResponses] = useState([]);
  
  // Show suggestions if current mode has no messages
  const showCurrentSuggestions = mode === 'esports' 
    ? esportsMessages.length === 0 
    : ejourneyMessages.length === 0;

  const getDeviceType = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
    if (/android/i.test(userAgent)) {
      return "Android";
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }
    return "Desktop";
  };
  
  useEffect(() => {
    // Initialize WaveSurfer
    waveSurferRef.current = WaveSurfer.create({
      container: waveContainerRef.current,
      waveColor: 'white',
      progressColor: 'white',
      cursorColor: 'white',
      height: 50,
      barWidth: 2,
      responsive: true,
      barGap: 3,
    });

    setDeviceType(getDeviceType());
    // Toggle play/pause when audio finishes
    waveSurferRef.current.on('finish', () => {
      setIsPlaying(false);
    });

    return () => {
      waveSurferRef.current?.destroy();
      cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      startListening(); // Start speech recognition
      setIsRecording(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream.current = stream;

      mediaRecorder.current = new MediaRecorder(stream);
      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };
      
      mediaRecorder.current.start();
      visualizeAudio(stream); // Initialize audio visualization
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = async () => {
    try{
    setIsRecording(false);
    mediaRecorder.current.onstop = async () => {
      const recordedBlob = new Blob(chunks.current, { type: 'audio/wav' });
      const url = URL.createObjectURL(recordedBlob);
      chunks.current = [];
      stopCanvasVisualizer();
        await handleSubmit(transcript, url, recordedBlob);
    };

    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
    }
    stopListening();
  }catch (error) {
    console.error('Error accessing microphone:', error);
  }
  };

  const visualizeAudio = (stream) => {
    // Initialize Audio Context and Analyser for real-time canvas visualization
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContextRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256; // Control resolution
    source.connect(analyserRef.current);
    drawLiveWaveform(); // Start drawing waveform
  };

  const drawLiveWaveform = () => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyserRef.current.getByteTimeDomainData(dataArray);
      canvasCtx.fillStyle = '#007AFF';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = '#ffffff';
      canvasCtx.beginPath();

      const sliceWidth = (canvas.width * 1.0) / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();

      // Request the next frame
      animationFrameIdRef.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const stopCanvasVisualizer = () => {
    cancelAnimationFrame(animationFrameIdRef.current);
    const canvasCtx = canvasRef.current.getContext('2d');
    canvasCtx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const toggleRecordingPlayback = (index) => {
    const waveSurfer = waveformRefs.current[index];
    const isCurrentlyPlaying = recordings[index].isPlaying;

    if (isCurrentlyPlaying) {
      waveSurfer.pause();
    } else {
      waveSurfer.play();
    }

    // Update the isPlaying state for the specific recording
    // setMessages((prevRecordings) =>
    //   prevRecordings.map((rec, i) =>
    //     i === index ? { ...rec, isPlaying: !isCurrentlyPlaying } : rec
    //   )
    // );
  };

  const initializeWaveform = (containerRef, url) => {
    const waveSurfer = WaveSurfer.create({
      container: containerRef,
      waveColor: '#FFFFFF',
      progressColor: '#222222',
      cursorColor: '#FFFFFF',
      height: 50,
      barWidth: 2,
      responsive: true,
      barGap: 3,
    });

    waveSurfer.load(url);
    return waveSurfer;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, responseMessages]);

  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toLocaleDateString('en-US', {
      day: '2-digit',
      month:`2-digit`
    });
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute:`2-digit`,
      hour12: false,
    });
    return `T${time}-D${date}`;
  };

  const getValidTimestamp = () => {
    const now = new Date();
    const lastSentTime = lastSentTimeRef.current;
  
    if (!lastSentTime) {
      const newTimestamp = getCurrentDateTime();
      lastTimestampRef.current = newTimestamp;
      lastSentTimeRef.current = now;
      setCurrentTimestamp(newTimestamp);
      setMessageCounter(1); // Reset counter for new session
      return newTimestamp;
    }
  
    const timeDiff = (now - lastSentTime) / 1000 / 60;
    if (timeDiff > 5) {   // Time limit to maintain the session_id in minutes
      const newTimestamp = getCurrentDateTime();
      lastTimestampRef.current = newTimestamp;
      lastSentTimeRef.current = now;
      setCurrentTimestamp(newTimestamp);
      setMessageCounter(1); // Reset counter for new session
      return newTimestamp;
    }
  
    lastSentTimeRef.current = now;
    return lastTimestampRef.current;
  };

  // Get subscription data from chatSubscription (from authProvider)
  const {
    daily_limit: dailyQuota = 100,
    chat_subscription_id: subscriptionType = 1
  } = chatSubscription || {};

  const openLinkInNewTab = () =>{
    const newTab = window.open(`https://adminweb.dev.gridgamers.com/#/admin/Analytics`, '_blank');
    if (newTab) {
        newTab.focus();
    } 
  }

  const uploadImage = async (e) => {
    const input = e.target;
    if (!input.files || input.files.length === 0) return;

    //pull out the file
    const selectedFile = input.files[0];

    // clear the input so selecting the same file again fires onChange
    input.value = "";

    // keep it in state for upload
    setBodyFormData(selectedFile);

    // preview url
    const fileUrl = URL.createObjectURL(selectedFile);
    setFile(fileUrl);
    setAllImages(prev => [...prev, fileUrl]);
  }
  
  const getMessageId = (sessionId, counter) => {
    // Format counter with leading zero if needed (M01, M02, etc.)
    const formattedCounter = String(counter).padStart(2, '0');
    return `${sessionId}-M${formattedCounter}`;
  };

  const handleSubmit = async (inputMessage, audiourl, recordedBlob) => {
    const msg = inputMessage !== undefined ? inputMessage : message;
  
    if (inputMessage === undefined && !recordedBlob) {
      setMessage('');  
    }

    // Get the current mode's setters (captured at submit time)
    const currentModeMessages = mode === 'esports' ? esportsMessages : ejourneyMessages;
    const setModeMessages = mode === 'esports' ? setEsportsMessages : setEjourneyMessages;
    const setModeResponses = mode === 'esports' ? setEsportsResponses : setEjourneyResponses;
  
    const currentSessionId = getValidTimestamp();
    const currentMessageId = getMessageId(currentSessionId, messageCounter);
    setMessageCounter(prev => prev + 1);
  
    // Audio recording
    if (recordedBlob && audiourl) {
      const newMessage = { imageUrl: file, text: msg, recodingUrl: audiourl };
      setModeMessages(prev => [...prev, newMessage]);
      setMessages([...messages, newMessage]);
      let audioResponse = "";
      
      try {
        setLoading(true);
        if (recordedBlob && !msg) {
          const formData = new FormData();
          formData.append("audio", recordedBlob, `audio.wav`);
          const userId = user?.id;
          const accessToken = Cookies.get("authToken");
          const uploadUrl = 
            `${CHAT_API_URL}upload-audio/` +
            `?session_id=${encodeURIComponent(currentSessionId)}` +
            `&user_id=${encodeURIComponent(userId)}` +
            `&daily_qouta=${dailyQuota}` +
            `&message_id=${encodeURIComponent(currentMessageId)}` +
            `&subscription_type=${subscriptionType}`;
          console.log('upload-audio url:', uploadUrl);
          const uploadRes = await axios.post(uploadUrl, formData, {
            headers: { 
              "Content-Type": "multipart/form-data",
              "Authorization": `Bearer ${accessToken}`
            }
          });
          audioResponse = uploadRes.data || "No response from the server";
        }
        
        const resAudioMessage = { text: (msg || audioResponse), recodingUrl: audiourl };
        setMessages([...messages, resAudioMessage]);
        const userId = user?.id;
        const accessToken = Cookies.get("authToken");
        const urlLink = 
          `${CHAT_API_URL}rag-agent` +
          `?session_id=${encodeURIComponent(currentSessionId)}` +
          `&user_id=${userId}` +
          `&daily_qouta=${dailyQuota}` +
          `&message_id=${encodeURIComponent(currentMessageId)}` +
          `&subscription_type=${subscriptionType}`;
        
        // Use fetch for streaming support
        const res = await fetch(urlLink, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ text: (msg || audioResponse) })
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        // Handle streaming response
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let fullResponse = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          fullResponse += chunk;
        }

        console.log("rag-agent response:", fullResponse);
        const responseWords = fullResponse.split(' ');
        streamResponse(responseWords);
        setLoading(false);
        setTranscript("");
  
        try {
          const history = await getUserSessionHistory({
            userId,
            sessionId: currentSessionId,
            page: 1,
            size: 100,
          });
          setSideMessages(history);
        } catch(err) {
          console.log("couldn't load sidebar history", err);
        }
      } catch(error) {
        console.error("Error fetching data from backend:", error);
        setResponseMessages([...responseMessages, "We apologize for the inconvenience. As this system is currently in beta, we are actively developing to improve your user experience. Please try again shortly."]);
        setLoading(false);
      }
    }
              // Image upload with optional text
          // Image upload with optional text
          else if (file && bodyFormData) {
            const newMessage = { imageUrl: file, text: msg };
            setModeMessages(prev => [...prev, newMessage]);
            setMessages([...messages, newMessage]);
            setFile(null);
            setBodyFormData(null);
            
            try {
              setLoading(true);
              const formData = new FormData();
              formData.append("image", bodyFormData);
              
              const userId = user?.id;
              const accessToken = Cookies.get("authToken");
              const caption = msg ? msg.trim() : "";
              const safeQuery = caption || "Can you explain this image?";
              
              // Match the cURL parameter order
              const url = 
                `${CHAT_API_URL}upload-image` + 
                `?user_id=${encodeURIComponent(userId)}` +
                `&session_id=${encodeURIComponent(currentSessionId)}` +
                `&message_id=${encodeURIComponent(currentMessageId)}` +
                `&query=${encodeURIComponent(safeQuery)}` +
                `&daily_qouta=${dailyQuota}` +
                `&subscription_type=${subscriptionType}`;
              
              console.log("upload-image url:", url);
              
              const response = await fetch(url, {
                method: "POST",
                body: formData,
                headers: {
                  "Authorization": `Bearer ${accessToken}`,
                  "accept": "application/json"
                }
                // Remove "stream: true" - not a valid fetch option
              });
              
              console.log("upload-image response status:", response.status);
              
              if (response.status === 429) {
                setLoading(false);
                setMessages(prev => prev.slice(0, -1));
                setFile(null);
                setBodyFormData(null);
                toast({
                  title: "Daily image upload limit exceeded",
                  status: "error",
                  position: "top",
                  isClosable: true,
                });
                return;
              }

              if (response.status === 401) {
                setLoading(false);
                setMessages(prev => prev.slice(0, -1));
                setFile(null);
                setBodyFormData(null);
                toast({
                  title: "Please update your subscription plan for image uploads",
                  status: "error",
                  position: "top",
                  isClosable: true,
                });
                return;
              }

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              // Properly read the streaming response
              const reader = response.body.getReader();
              const decoder = new TextDecoder("utf-8");
              let fullResponse = "";

              // Read ALL chunks until done
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                fullResponse += chunk;
              }

              console.log("upload-image full response:", fullResponse);

              // Stream the response to UI
              const responseWords = fullResponse.split(' ');
              streamResponse(responseWords);
              setLoading(false);
              setImageSubmitted(false);

              // Load session history
              try {
                const history = await getUserSessionHistory({
                  userId,
                  sessionId: currentSessionId,
                  page: 1,
                  size: 100,
                });
                setSideMessages(history);
              } catch(err) {
                console.error("couldn't load sidebar history", err);
              }
              
            } catch(error) {
              console.error("Error fetching data from backend:", error);
              
              // Fixed: check error.message for status codes, not axios
              if (error.message?.includes("429")) {
                setLoading(false);
                setMessages(prev => prev.slice(0, -1));
                setMessage("");
                setIsQuotaExceeded(true);
                setShowSuggestions(false);
                setFile(null);
                setBodyFormData(null);
                toast({
                  title: "Daily request limit exceeded",
                  status: "error",
                  position: "top",
                  isClosable: true,
                });
                return;
              }
              
              setResponseMessages([...responseMessages, "We apologize for the inconvenience. As this system is currently in beta, we are actively developing to improve your user experience. Please try again shortly."]);
              setLoading(false);
              setImageSubmitted(false);
              setFile(null);
              setBodyFormData(null);
            }
          }
              // Text-only messages
              else if (msg && !file && !bodyFormData && !recordedBlob && !audiourl) {
                setFile(null);
                setBodyFormData(null);
                
                setLoading(true);
                if (editIndex !== null) {
                  const newMessage = { imageUrl: null, text: msg };
                  setModeMessages(prev => [...prev, newMessage]);
                  setMessages([...messages, newMessage]);
                  setEditIndex(null);
                  setMessage('');
            
                  try {
                    const userId = user?.id;
                    const history = await getUserSessionHistory({
                      userId,
                      sessionId: currentSessionId,
                      page: 1,
                      size: 100,
                    });
                    setSideMessages(history);
                  } catch (e) {}
                } else {
                  const newMessage = { imageUrl: null, text: msg };
                  setModeMessages(prev => [...prev, newMessage]);
                  setMessages([...messages, newMessage]);
                  setMessage('');
                }
                setMessage('');
            
                if (msg === "I want to see the analytics for texas high school league") {
                  setTimeout(() => {
                    const msg1 = ['In', '2024,', 'one', 'of', 'the', 'prominent', 'esports', 'league', 'in', 'the', 'Texas', 'United', 'States', 'is', 'the', 'Esports', '&', 'Games', 'League.', 'Here', 'are', 'some', 'details:\n\n-', '**Taxes', 'Esports', '&', 'Games', 'League', '2024**:', '\n', '', '-', '**Location**:', 'Taxes,', 'United', 'States', 'America.\n', '', '-', '**Features**:', 'The', 'event', 'will', 'feature', 'over', '10', 'games', 'played', 'on', '80', 'machines.\n', '', '-', '**Goal**:', 'It', 'aims', 'to', 'exceed', 'the', 'previous', "year's", 'attendance,', 'making', 'it', 'a', 'major', 'attraction', 'for', 'esports', 'enthusiasts.\n\nFor', 'more', 'detailed', 'information,', 'you', 'can', 'check', 'resources', 'like', '[GridGamers.com](https://adminweb.dev.gridgamers.com/#/admin/Analytics/)'];
                    streamResponse(msg1);
                    setLoading(false);
                    setImageSubmitted(false);
                  }, 2000);
                  setTimeout(() => {
                    openLinkInNewTab();
                  }, 8000);
                } else {
                  try {
                    const userId = user?.id;
                    console.log('user id:', userId);
                    const accessToken = Cookies.get("authToken");
                    const url = 
                      `${CHAT_API_URL}rag-agent` +
                      `?session_id=${encodeURIComponent(currentSessionId)}` +
                      `&user_id=${userId}` +
                      `&daily_qouta=${dailyQuota}` +
                      `&message_id=${encodeURIComponent(currentMessageId)}` +
                      `&subscription_type=${subscriptionType}`;
                    console.log("rag-agent url:", url);
                    const payload = { text: msg };
            
                    console.log("[rag-agent] calling:", url, "payload:", payload);
                    
            // Use fetch for streaming support instead of axios
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${accessToken}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          });

          if (response.status === 429) {
            throw new Error("Rate limit exceeded");
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          console.log("[rag-agent] response status:", response.status);

          // Handle streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let fullResponse = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value);
            fullResponse += chunk;
          }

          console.log("[rag-agent] full response:", fullResponse);

          const responseWords = fullResponse.split(' ');
          streamResponse(responseWords);
          setLoading(false);
          setImageSubmitted(false);
          setEditIndex(null);
  
          const history = await getUserSessionHistory({
            userId,
            sessionId: currentSessionId,
            page: 1,
            size: 100,
          });
          console.log("[user-session-history] payload:", history);
          setSideMessages(history);
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 429) {
            setLoading(false);
            setMessages(prev => prev.slice(0, -1));
            setMessage("");
            setIsQuotaExceeded(true);
            setShowSuggestions(true);
            toast({
              title: "Daily request limit exceeded",
              status: "error",
              position: "top",
              isClosable: true,
            });
            return;
          }
          console.error("Error fetching data from backend:", error);
          setResponseMessages([...responseMessages, "We apologize for the inconvenience. As this system is currently in beta, we are actively developing to improve your user experience. Please try again shortly."]);
          setLoading(false);
          setFile(null);
          setImageSubmitted(false);
        }
      }
    }
    else {
      console.log("No valid input provided");
      return;
    }
  };
  
  const streamResponse = (data) => {
    let currentResponse = '';
    
    // Capture which mode's setter to use at the start
    const setModeResponses = mode === 'esports' ? setEsportsResponses : setEjourneyResponses;
    
    // Add a placeholder for this response
    setModeResponses(prev => [...prev, '']);
  
    data.forEach((word, index) => {
      setTimeout(() => {
        currentResponse += word + ' ';
        const newResponse = marked(currentResponse).replace(/<a /g, '<a target="_blank" ')
        
        // Update context (for compatibility)
        setResponseMessages([...responseMessages,
          newResponse,
        ]);
        
        // Update mode-specific responses (last item)
        setModeResponses(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = newResponse;
          return updated;
        });
        
        scrollToBottom();
      }, index * 60);
    });
  };
  
  const handleSelectPrompt = async (prompt) => {
    setShowSuggestions(false);
    setMessage(''); 
    await handleSubmit(prompt);
    if (textareaRef.current) {
      textareaRef.current.focus();
      adjustTextareaHeight(); 
    }
  };

  const handleEdit = (index) => {
    const currentModeMessages = mode === 'esports' ? esportsMessages : ejourneyMessages;
    setMessage(currentModeMessages[index]?.text);
    setEditIndex(index);

    setTimeout(() => {
      textareaRef.current.focus(); 
      adjustTextareaHeight(); 
    }, 0); 
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setTimeout(() => {
        resetTextareaHeight();
        setShowSuggestions(false);
        handleSubmit();
      },0)
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(newHeight, 150)}px`;
      textarea.style.overflowY = newHeight > 150 ? 'auto' : 'hidden';
    }
  };

  const resetTextareaHeight = () => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = 'auto'; 
      textarea.rows = 1;
      textarea.style.overflowY = 'hidden'; 
    }
  };

  const esportsSuggestions = [
    "What strategies do pro players use to stay ahead?",
    "Best action games on PS5",
    "What is PUBG?",
    "What is a single stage tournament?"
  ];

  const ejourneySuggestions = [
    "Show me top places to visit in Makkah",
    "Hotels near esports venues in Doha",
    "Tips for planning a Hajj package",
    "Help me plan an itinerary for Medina"
  ];

  const suggestions = mode === 'esports' ? esportsSuggestions : ejourneySuggestions;
  
  // Get current mode's messages for rendering
  const displayMessages = mode === 'esports' ? esportsMessages : ejourneyMessages;
  const displayResponses = mode === 'esports' ? esportsResponses : ejourneyResponses;

  return (
    <>
      <Flex direction="column" align="center" p={{lg:"6", sm:"0px"}} bg="transparent" w="-webkit-fill-available" maxW="1000px">
        {/* Mode Toggle - Esports / eJourney */}
        <Box mb={4} mt={2}>
          <ModeToggle mode={mode} setMode={setMode} />
        </Box>
        
        {/* Show suggestions if current mode has no messages */}
        {showCurrentSuggestions && (
        <Box mb={4} width="100%">
          <Flex justifyContent="center" mt="3%" mb="3%">

          <GridGLogo
            height="65px"
            width="65px"
          />

          </Flex>
          <Text fontWeight="bold" mb={2} textAlign="center" marginBottom="5">You may want to ask:</Text>
          <SimpleGrid columns={{lg:"4",sm:"2"}} spacing={2}>
            {suggestions.map((suggestion, index) => (
              <>
              
              <Box
                key={index}
                border="1px solid #E2E8F0"
                borderRadius="md"
                p={4}
                cursor="pointer"
                bg="white"
                _hover={{ bg: "blue.50", transform: "scale(1.05)" }}
                transition="all 0.2s ease-in-out"
                onClick={() => { 
                  setShowSuggestions(false);
                  setTimeout(() => {
                    textareaRef.current.focus(); 
                    handleSelectPrompt(suggestion)
                    adjustTextareaHeight(); 
                  }, 0);
                }}
              >
                <SuggestionsVector/>
                <Text textAlign="start">{suggestion}</Text>
              </Box>
              </>
            ))}
            </SimpleGrid>
          </Box>
        )}
        <Flex mb="100px" w="-webkit-fill-available" direction="column"> 
          {displayMessages.map((msg, index) => (
            <React.Fragment key={`message-${index}`}>
              <Flex key={`user-message-${index}`} alignItems="flex-start">
                <Avatar
                  src={UserSearchImg}
                  w='35px'
                  h='35px'
                  me='10px'
                  mb="10px"
                  bg="transparent"
                  display={{
                    lg:"inline",
                    sm:"none",
                  }}
                />
                <MobileUserLogo
                  w='33px'
                  h='33px'
                  me='10px'
                  mb="10px"
                  bg="transparent"
                  display={{
                    lg:"none",
                    md:"inline"
                  }}
                />
                
                <Flex direction="column" flex="1" align="flex-start">
                  {msg.imageUrl && (
                    <Box mb={msg.text ? 3 : 0}>
                      <img 
                        src={msg?.imageUrl} 
                        alt="Uploaded" 
                        style={{  
                          marginBottom: msg.text ? '0' : '1rem',
                          height:'100%',
                          width:'100%',
                          maxHeight:"18rem",
                          maxWidth:"30rem",
                          borderRadius:'10px',
                          objectFit:'cover'
                        }}
                      />
                    </Box>
                  )}
                  
                  {msg.recodingUrl ? (
                    <div 
                      style={{
                        maxWidth: "1000px",
                        flexDirection: "column",
                        width: "-webkit-fill-available",
                        display: "contents",
                      }}
                    >
                      <div key={index} style={{ marginBottom: '20px', width:"100%" }}>
                        <div
                          ref={(el) => {
                            if (el && !waveformRefs.current[index]) {
                              waveformRefs.current[index] = initializeWaveform(el, msg?.recodingUrl);
                            }
                          }}
                          style={{
                            height: '50px',
                            borderRadius: '50px',
                            backgroundColor: '#298BE2',
                            position: 'relative',
                            marginBottom: '10px',
                          }}
                        ></div>
                        <Box p={2} display="flex" flexDirection="column" w="-webkit-fill-available">
                          <Text id="textContentDiv">{msg?.text}</Text>
                        </Box>
                        <button
                          onClick={() => toggleRecordingPlayback(index)}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#007AFF',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'none',
                          }}
                        >
                          {/* {recording.isPlaying ? '⏸ Pause' : '▶️ Play'} */}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {msg.text && (
                        <Flex
                          w="100%"
                          maxW="1000px"
                          direction="column"
                          border="1px solid #e2e8f0"
                          borderRadius="lg"
                          p={1}
                          mb={4}
                          bg="transparent"
                        >
                          <Box p={2} display="flex" justifyContent="space-between" w="-webkit-fill-available">
                            <Text id="textContentDiv">{msg?.text}</Text>
                          </Box>
                        </Flex>
                      )}
                    </>
                  )}
                </Flex>
                
                {!msg.imageUrl && !msg.recodingUrl && (
                  <EditIcon
                    onClick={() => handleEdit(index)}
                    display={{
                      lg: "initial",
                      sm: "none",
                    }}
                    w="30px"
                    h="30px"
                    cursor="pointer"
                    border="1px solid #dddddd"
                    borderRadius="full"
                    padding="1"
                    ms="10px"
                    mb="10px"
                  />
                )}
              </Flex>

              <Flex key={`ai-response-${index}`}>
                <Avatar
                  src={GPTresposeImg}
                  w='30px'
                  h='30px'
                  me='10px'
                />
                {loading && index === displayMessages.length - 1 ? ( 
                  <>
                    <LoadingModal2
                      isOpen={loading}
                    />
                  </>
                ) : (
                  <Flex
                    w="-webkit-fill-available"
                    maxW="1000px"
                    direction="column"
                    border="1px solid #e2e8f0"
                    borderRadius="lg"
                    mb={4}
                    bg="white"
                    boxShadow="md"
                    padding={{
                      lg:"30px",
                      sm:"3"
                    }}
                    paddingLeft={{
                      sm:"7"
                    }}
                  >
                    <Box bg="white" borderRadius="md">
                      {/* Updated to use ResponseContent component for place cards */}
                      <ResponseContent 
                        content={displayResponses[index]} 
                        messagesEndRef={messagesEndRef}
                      />
                    </Box>
                  </Flex>
                )}
              </Flex>
            </React.Fragment>
          ))}
        </Flex>
      </Flex>

      <Flex 
        w="-webkit-fill-available" 
        maxW="1000px"
        align="center" 
        position="fixed" 
        bg="#f7f8fc" 
        bottom="0px"
        px={{
          xl:"50px",
          sm:"15"
        }}
        pb={{
          xl:"100px",
          sm:"20px"
        }}
      >
        <canvas
            ref={canvasRef}
            height="50"
            style={{
              borderRadius: '20px',
              backgroundColor: '#298BE2',
              display: isRecording ? 'block' : 'none',
              marginBottom: '10px',
              position: 'relative',
              zIndex:"111"
            }}
          />
        {!isRecording &&
        <>
        <MobileImgUpload
          display={{
            lg:"none",
            sm:"inline"
          }}
          h="35px"
          w="35px"
          bg="transparent"
          marginRight="10px"
          onClick={() => document.getElementById('file-input').click()}
          cursor='pointer'
        />
        <Box 
          w="100%" 
          position="relative" 
          bg="#f7f8fc"
          borderRadius="26px"
          overflow="hidden" 
          borderColor="#E2E8F0"
        >
           <input 
              ref={fileInputRef}
              id="file-input"
              type="file" 
              onChange={uploadImage}
              style={{ display: "none" }}
              accept=".png, .jpeg, .jpg, .webp, .gif" 
            />

            {file && 
              <>
             <ImgCrossIcon
              zIndex="10"
              position="absolute"
              left="90px"
              onClick={()=>{
                setFile(null)
                setBodyFormData(null)
              }}
              cursor="pointer"
             /> 
              <img 
                src={file} 
                alt="Uploaded" 
                style={{  
                  zIndex:"111", 
                  position:"relative",
                  margin:'10px 15px 5px 40px',
                  height:'56px',
                  width:'56px',
                  borderRadius:'10px',
                  objectFit:'cover'
                  }}
               />
              </>
              } 
          <Flex alignItems={"center"} marginLeft={"15px"}>
          <UploadImagesChat
            display={{
              lg: "inline",
              sm: "none"
            }}
            bg="transparent"
            zIndex="999"
            position="relative"
            onClick={() => fileInputRef.current?.click()}
            cursor="pointer"
          />
          <Textarea
            ref={textareaRef}
            position="relative"  
            zIndex="1"  
            flex="1"
            border={"none"}
            placeholder={editIndex !== null ? "Edit your message" : "Send a message"}
            size="lg"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              adjustTextareaHeight();  
            }}
            onKeyDown={handleKeyDown}
            rows={1} 
            maxHeight="150px"
            resize="none"
            overflowY="hidden"
            bg="transparent"
            padding="10px"
            outline="none"
            boxShadow="none"
            _focus={{
              outline: "none",
              boxShadow: "none"
            }}
            disabled={isQuotaExceeded}
          />
          <div
            ref={waveContainerRef}
          ></div>
          
          {!file && 
          <MobileVoiceUpload
            h="30px"
            w="30px"
            bg="transparent"
            marginRight="10px"
            zIndex="99"
            onClick={(event) => {
              isRecording ? stopRecording() : startRecording()
            }}
            cursor="pointer"
            disabled={file}
            display={{
              lg:"none",
              sm:"block"
            }}
          /> 
          }
          
          </Flex>
          <Box 
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="white"
            zIndex="0"
            overflowY="auto" 
          >
            <Box height="100%" />
          </Box>
        </Box>
        </>
        }
        {size === "lg" ?
          <Button
          ml={4}
          bg="#298BE2"
          color="white"
          borderRadius="full"
          px={8}
          h="48px"
          _hover={{
            bg: "#6eb2ef",
          }}
          _active={{
            bg: "#298BE2",
          }}
          onClick={() => {
            if (isRecording) {
              stopRecording();
              resetTextareaHeight();
            }
            resetTextareaHeight();
            setShowSuggestions(false);
            handleSubmit();
            setEditIndex(null);
          }}
          isDisabled={isQuotaExceeded || (message.trim() === "" && !file)}
        >
          {editIndex !== null ? "Update" : "Submit"}
        </Button>
          : 
          <QuerySendButton
            ml="10px"
            height="32px"
            width="32px"
            onClick={() => {
              handleSubmit();
              resetTextareaHeight();
              setShowSuggestions(false);
              if (isRecording) {
                stopRecording();
                resetTextareaHeight();
              }
            }}
          />
        }
      </Flex>
    </>
  );
};

export default ChatInterface;